import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Box, 
  Avatar, 
  Menu, 
  MenuItem, 
  Divider,
  Chip,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Menu as MenuIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  EmojiEvents as BadgeIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const drawerWidth = 72;
const collapsedDrawerWidth = 72;

const Header = ({ onMenuClick, sidebarOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfileNavigate = () => {
    navigate('/profile');
    handleClose();
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const getUserInitials = (username) => {
    if (!username) return 'U';
    return username.charAt(0).toUpperCase();
  };

  const getUserBadge = () => {
    // You can customize this based on user data
    return 'Fighter';
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        // Desktop: positioned next to sidebar
        width: { 
          xs: '100%', // Full width on mobile
          md: `calc(100% - ${sidebarOpen ? drawerWidth : collapsedDrawerWidth}px)` // Next to sidebar on desktop
        },
        ml: { 
          xs: 0, // No margin on mobile
          md: `${sidebarOpen ? drawerWidth : collapsedDrawerWidth}px` // Margin for sidebar on desktop
        },
        transition: 'width 0.3s ease, margin-left 0.3s ease',
        background: 'transparent',
        boxShadow: 'none',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onMenuClick}
          sx={{ 
            mr: 2,
            display: { xs: 'block', md: 'none' } // Show only on mobile/tablet
          }}
        >
          <MenuIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }} />
        {user && (
          <>
            <IconButton
              onClick={handleProfileClick}
              sx={{ p: 0 }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  background: 'linear-gradient(135deg, #6b46c1 0%, #8b5cf6 100%)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '1rem',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 4px 15px rgba(107, 70, 193, 0.3)',
                  }
                }}
              >
                {getUserInitials(user.username)}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  background: 'linear-gradient(135deg, rgb(30, 30, 30) 0%, rgb(35, 34, 38) 50%)',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  borderRadius: 2,
                  minWidth: 220,
                  mt: 1.5,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  '& .MuiMenuItem-root': {
                    borderRadius: 1,
                    mx: 1,
                    my: 0.5,
                    color: '#ffffff',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.05)',
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Box sx={{ px: 2, py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      background: 'linear-gradient(135deg, #6b46c1 0%, #8b5cf6 100%)',
                      color: '#ffffff',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      mr: 1.5,
                    }}
                  >
                    {getUserInitials(user.username)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                      {user.username}
          </Typography>
                    <Chip
                      icon={<BadgeIcon sx={{ fontSize: 16 }} />}
                      label={getUserBadge()}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.75rem',
                        background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                        color: '#000000',
                        fontWeight: 600,
                        '& .MuiChip-icon': {
                          color: '#000000',
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.05)', mx: 1 }} />
              <MenuItem onClick={handleProfileNavigate}>
                <ListItemIcon>
                  <PersonIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </ListItemIcon>
                <ListItemText>
                  <Typography sx={{ color: '#ffffff' }}>User Profile</Typography>
                </ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </ListItemIcon>
                <ListItemText>
                  <Typography sx={{ color: '#ffffff' }}>Logout</Typography>
                </ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header; 